<template>
  <SchemaTablePage
    ref="tablePageRef"
    layout-mode="tab"
    show-summary
    :search-schema="searchSchema"
    :columns="tableColumns"
    :default-query="defaultQuery"
    :request="request"
    :build-params="buildParams"
    :parse-result="parseResult"
    :table-attrs="tableAttrs"
  >
    <template #search-amountRange="{ query }">
      <div class="amount-range" style="width: 100%">
        <el-input-number v-model="query.minOrderAmount" :controls="false" placeholder="最小金额" :min="0" />
        <div style="width: 10px" />
        <el-input-number v-model="query.maxOrderAmount" :controls="false" placeholder="最大金额" :min="0" />
      </div>
    </template>

    <template #colOrderNo="{ row }">
      <el-button type="text" size="mini" @click="openDetail(row.orderNo)">{{ row.orderNo }}</el-button>
    </template>
    <template #colMemberAccount="{ row }">
      <MemberAccountCell :row="row" :username="row.memberAccount" :uid="row.uid" :vip-level="row.memberLevel" />
    </template>
    <template #colOrderAmount="{ row }">{{ row.orderAmount | centsToYuan }}</template>
    <template #colArrivalAmount="{ row }">{{ row.arrivalAmount | centsToYuan }}</template>
    <template #colTopUpAmount="{ row }">{{ row.topUpAmount | centsToYuan }}</template>
    <template #colDepositDiscount="{ row }">{{ row.depositDiscount | centsToYuan }}</template>
    <template #colPayinMethod="{ row }">{{ payinMethodLabel(row.payin_method) }}</template>
    <template #colStatus="{ row }">
      <el-tag size="mini" :type="getStatusTagType(row.status)">{{ row.status }}</el-tag>
    </template>

    <template #table-header>
      <el-button type="primary" size="mini" :loading="exporting" @click="handleExport">导出 Excel</el-button>
    </template>
  </SchemaTablePage>
</template>

<script>
import { exportPayinHistory, getPayinHistory } from '@/api/finance/moneyManagement'
import {
  PAYIN_EXPORT_COLUMNS,
  buildExportSheet,
  createPayinExportFormatCell,
  parseExportList,
} from '@/pages/admin/finance/utils/financeExport'
import { mapPayinHistoryRow } from '@/pages/admin/finance/utils/payinHistoryFormat'
import { buildExportFilename, downloadExcel } from '@/utils/exportExcel'
import { centsToYuan, yuanToCents } from '@/utils/amount'
import { toUnixSeconds } from '@/utils/time'
import { PAYIN_METHOD_OPTIONS } from './config'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'

export default {
  name: 'ManageDepositHistoryTab',
  components: { MemberAccountCell, SchemaTablePage },
  filters: {
    centsToYuan,
  },
  data() {
    return {
      PAYIN_METHOD_OPTIONS,
      exporting: false,
      defaultQuery: {
        memberAccount: '',
        orderNo: '',
        thirdOrderNo: '',
        minOrderAmount: undefined,
        maxOrderAmount: undefined,
        payin_method: '',
        orderStatus: '',
        orderTimeRange: [],
      },
      searchSchema: [
        {
          prop: 'memberAccount',
          label: '会员账号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'orderNo',
          label: '订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'thirdOrderNo',
          label: '三方订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          key: 'amountRange',
          label: '订单金额',
          component: 'slot',
          slot: 'search-amountRange',
        },
        {
          prop: 'payin_method',
          label: '支付方式',
          component: 'select',
          componentProps: { clearable: true },
          options: [{ label: '全部', value: '' }, ...PAYIN_METHOD_OPTIONS],
        },
        {
          prop: 'orderStatus',
          label: '订单状态',
          component: 'select',
          componentProps: { clearable: true },
          options: [
            { label: '全部', value: '' },
            { label: '存款成功', value: '存款成功' },
            { label: '已取消', value: '已取消' },
          ],
        },
        {
          prop: 'orderTimeRange',
          label: '订单时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          span: { xs: 24, sm: 24, md: 12, lg: 10 },
        },
        { component: 'actions' },
      ],
      tableColumns: [
        { prop: 'orderNo', label: '订单号', width: 180, resizable: false },
        { prop: 'thirdOrderNo', label: '三方订单号', minWidth: 200, resizable: false },
        { prop: 'memberAccount', label: '会员账号', width: 140, resizable: false, slot: 'colMemberAccount' },
        { prop: 'orderAmount', label: '订单金额', minWidth: 100, resizable: false, slot: 'colOrderAmount' },
        { prop: 'total_fee', label: '手续费', minWidth: 60, resizable: false },
        { prop: 'arrivalAmount', label: '到账金额', minWidth: 100, resizable: false, slot: 'colArrivalAmount' },
        { prop: 'topUpAmount', label: '上分金额', minWidth: 100, resizable: false, slot: 'colTopUpAmount' },
        { prop: 'depositDiscount', label: '存款赠送', minWidth: 100, resizable: false, slot: 'colDepositDiscount' },
        { prop: 'payment_channel', label: '支付通道', minWidth: 100, resizable: false },
        { prop: 'payin_method', label: '支付方式', minWidth: 100, resizable: false, slot: 'colPayinMethod' },
        { prop: 'orderTime', label: '订单时间', minWidth: 160, resizable: false },
        { prop: 'completeTime', label: '完成时间', minWidth: 160, resizable: false },
        {
          fixed: 'right',
          prop: 'status',
          label: '状态',
          minWidth: 100,
          align: 'center',
          resizable: false,
          slot: 'colStatus',
        },
      ],
      tableAttrs: {
        showSummary: true,
        summaryMethod: (...args) => this.getSummaries(...args),
      },
    }
  },
  methods: {
    request(params) {
      return getPayinHistory(params)
    },
    payinMethodLabel(value) {
      const hit = this.PAYIN_METHOD_OPTIONS.find(item => item.value === value)
      return hit ? hit.label : ''
    },
    getStatusTagType(status) {
      const tagTypeMap = {
        待确认: 'warning',
        存款成功: 'success',
        已取消: 'info',
      }
      return tagTypeMap[status] || 'info'
    },
    getPayinStatusParam(orderStatus) {
      const map = { 存款成功: 5, 已取消: 4 }
      return orderStatus ? (map[orderStatus] !== undefined ? map[orderStatus] : 0) : 0
    },
    buildFilterParams(query) {
      const params = {
        status: this.getPayinStatusParam(query.orderStatus),
      }
      if (query.memberAccount) params.username = query.memberAccount
      if (query.orderNo) params.order_id = query.orderNo
      if (query.thirdOrderNo) params.third_order_id = query.thirdOrderNo
      if (query.minOrderAmount > 0) params.order_amount_start = yuanToCents(query.minOrderAmount)
      if (query.maxOrderAmount > 0) params.order_amount_end = yuanToCents(query.maxOrderAmount)
      if (query.payin_method) params.payin_method = query.payin_method
      if (Array.isArray(query.orderTimeRange) && query.orderTimeRange.length === 2) {
        const [start, end] = query.orderTimeRange
        const startAt = toUnixSeconds(start)
        const endAt = toUnixSeconds(end)
        if (startAt) params.start_at = startAt
        if (endAt) params.end_at = endAt
      }
      return params
    },
    buildParams({ query, page, limit }) {
      return {
        page,
        page_size: limit,
        ...this.buildFilterParams(query),
      }
    },
    buildExportParams(query) {
      return this.buildFilterParams(query)
    },
    async handleExport() {
      const query = this.$refs.tablePageRef?.query
      if (!query) return
      this.exporting = true
      try {
        const res = await exportPayinHistory(this.buildExportParams(query))
        const list = parseExportList(res)
        if (!list.length) {
          this.$message.warning('暂无数据可导出')
          return
        }
        const formatCell = createPayinExportFormatCell()
        const { header, data } = buildExportSheet(PAYIN_EXPORT_COLUMNS, list, formatCell)
        await downloadExcel({
          header,
          data,
          filename: buildExportFilename('存款记录'),
        })
        this.$message.success('导出成功')
      } catch (err) {
        this.$message.error(err?.message || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    parseResult(response) {
      const respData = (response && response.data) || {}
      const list = respData.list || []
      return {
        list: list.map(mapPayinHistoryRow),
        total: respData.total || 0,
      }
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        const columnKeys = ['orderAmount', 'arrivalAmount', 'topUpAmount', 'depositDiscount']
        if (columnKeys.includes(column.property)) {
          const values = data.map(item => parseFloat(item[column.property]) || 0)
          sums[index] = values.reduce((prev, curr) => {
            const value = parseFloat(curr)
            if (!isNaN(value)) {
              return prev + curr
            }
            return prev
          }, 0)
          sums[index] = centsToYuan(sums[index])
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
  },
}
</script>

<style scoped>
.amount-range {
  display: flex;
  align-items: center;
  width: 100%;
}

.amount-range .el-input-number {
  flex: 1;
  min-width: 0;
}
</style>
